import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FiTrendingUp,
  FiDollarSign,
  FiAlertCircle,
} from "react-icons/fi";
import { Users, CreditCard, TrendingUp, AlertCircle, DollarSign } from "lucide-react";

const Billing = ({ studentId }) => {
  const [billingAccount, setBillingAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState({});

  useEffect(() => {
    if (!studentId) return;

    const fetchBilling = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/billing/student/${studentId}`
        );

        const data = {
          StudentFullName: res.data.studentFullName,
          StudentNumber: res.data.studentNumber,
          IdNumber: res.data.idNumber,
          ExpectedAmount: res.data.expectedAmount ?? 0,
          TotalPaid: res.data.totalPaid ?? 0,
          OutstandingAmount: res.data.outstandingAmount ?? 0,
          DepositAmount: res.data.depositAmount ?? 0,
          MonthlyBillings:
            res.data.monthlyBillings?.map((m) => ({
              MonthlyBillingId: m.monthlyBillingId,
              Year: m.year,
              Month: m.month,
              MonthlyAmount: m.monthlyAmount ?? 0,
              AmountPaid: m.amountPaid ?? 0,
              Status: m.status ?? "Unpaid",
            })) || [],
        };

        setBillingAccount(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchBilling();
  }, [studentId]);

  const handleAmountChange = (billingId, value) =>
    setPayments((prev) => ({ ...prev, [billingId]: value }));

  const handlePay = async (monthlyBillingId) => {
    const amount = parseFloat(payments[monthlyBillingId]);
    if (isNaN(amount) || amount <= 0) return alert("Enter valid amount");

    try {
      const res = await axios.put(
        `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/billing/pay/${monthlyBillingId}`,
        { amount }
      );

      setBillingAccount((prev) => {
        const updatedMonthly = prev.MonthlyBillings.map((m) =>
          m.MonthlyBillingId === monthlyBillingId
            ? { ...m, AmountPaid: amount, Status: res.data.status ?? "Paid" }
            : m
        );

        const totalPaid = updatedMonthly.reduce(
          (sum, m) => sum + (m.AmountPaid ?? 0),
          0
        );

        return {
          ...prev,
          MonthlyBillings: updatedMonthly,
          TotalPaid: totalPaid,
          OutstandingAmount: prev.ExpectedAmount - totalPaid,
        };
      });

      setPayments((prev) => ({ ...prev, [monthlyBillingId]: "" }));
    } catch (err) {
      console.error(err);
    }
  };

  const handleClear = async (monthlyBillingId) => {
    try {
      const res = await axios.put(
        `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/billing/clear/${monthlyBillingId}`
      );

      setBillingAccount((prev) => {
        const updatedMonthly = prev.MonthlyBillings.map((m) =>
          m.MonthlyBillingId === monthlyBillingId
            ? {
              ...m,
              AmountPaid: res.data.amountPaid ?? 0,
              Status: res.data.status ?? "Unpaid",
            }
            : m
        );

        return {
          ...prev,
          MonthlyBillings: updatedMonthly,
          TotalPaid: res.data.totalPaid,
          OutstandingAmount: res.data.outstandingAmount,
        };
      });

      setPayments((prev) => ({ ...prev, [monthlyBillingId]: "" }));
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-10 h-10 border-4 border-gray-200 border-t-gray-800 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-gray-500">Loading billing data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!billingAccount) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="text-center py-12">
          <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-xl bg-gray-100">
            <AlertCircle className="w-6 h-6 text-gray-400" />
          </div>
          <h2 className="text-base font-semibold text-gray-900">No Billing Record Found</h2>
          <p className="text-sm text-gray-500 mt-1">This student does not yet have a billing account created.</p>
        </div>
      </div>
    );
  }

  const acc = billingAccount;
  const totalCollected = (acc.TotalPaid ?? 0) + (acc.DepositAmount ?? 0);
  const paidPercent = (totalCollected / (acc.ExpectedAmount ?? 1)) * 100;

  const KpiCard = ({ title, amount, icon, iconBg }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-all duration-200">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            R{(amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </p>
        </div>
        <div className={`p-2 ${iconBg} rounded-lg`}>
          {icon}
        </div>
      </div>
    </div>
  );

  const StatusBadge = ({ status }) => {
    const statusStyles = {
      Paid: "bg-green-100 text-green-700",
      Unpaid: "bg-red-100 text-red-700",
      Partial: "bg-amber-100 text-amber-700",
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${statusStyles[status] || statusStyles.Unpaid}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Billing Overview</h2>
              <p className="text-sm text-gray-500 mt-0.5">Student financial account management</p>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard 
          title="Expected Revenue" 
          amount={acc.ExpectedAmount} 
          icon={<TrendingUp className="w-5 h-5 text-blue-600" />}
          iconBg="bg-blue-50"
        />
        <KpiCard 
          title="Deposit Amount" 
          amount={acc.DepositAmount} 
          icon={<DollarSign className="w-5 h-5 text-purple-600" />}
          iconBg="bg-purple-50"
        />
        <KpiCard 
          title="Total Collected" 
          amount={totalCollected} 
          icon={<CreditCard className="w-5 h-5 text-green-600" />}
          iconBg="bg-green-50"
        />
        <KpiCard 
          title="Outstanding Balance" 
          amount={acc.OutstandingAmount} 
          icon={<AlertCircle className="w-5 h-5 text-amber-600" />}
          iconBg="bg-amber-50"
        />
      </div>

      {/* Progress Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-gray-700">Revenue Collection Progress</h3>
          <span className="text-sm font-semibold text-gray-900">{paidPercent.toFixed(0)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="h-2 rounded-full bg-gray-800 transition-all duration-500"
            style={{ width: `${Math.min(paidPercent, 100)}%` }}
          />
        </div>
      </div>

      {/* Student Profile Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-gray-600" />
            <h3 className="text-sm font-semibold text-gray-900">Student Profile</h3>
          </div>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Full Name</p>
              <p className="text-sm font-medium text-gray-900 mt-1">{acc.StudentFullName}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Student Number</p>
              <p className="text-sm font-medium text-gray-900 mt-1">{acc.StudentNumber}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">ID Number</p>
              <p className="text-sm font-medium text-gray-900 mt-1">{acc.IdNumber}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Billing Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Period</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Payment</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {acc.MonthlyBillings.map((m) => (
                <tr key={m.MonthlyBillingId} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-4">
                    <span className="text-sm font-medium text-gray-900">
                      {m.Year}-{String(m.Month).padStart(2, "0")}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span className="text-sm font-semibold text-gray-900">
                      R{m.MonthlyAmount.toFixed(2)}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="relative w-32">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">R</span>
                      <input
                        type="number"
                        disabled={m.Status === "Paid"}
                        value={payments[m.MonthlyBillingId] ?? (m.AmountPaid > 0 ? m.AmountPaid.toFixed(2) : "")}
                        onChange={(e) => handleAmountChange(m.MonthlyBillingId, e.target.value)}
                        className="w-full pl-7 pr-3 py-1.5 text-sm text-gray-900 placeholder-gray-400 bg-white border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all disabled:bg-gray-50 disabled:text-gray-500"
                        placeholder="0.00"
                      />
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <StatusBadge status={m.Status} />
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handlePay(m.MonthlyBillingId)}
                        disabled={m.Status === "Paid"}
                        className="px-3 py-1.5 text-xs font-medium rounded-md bg-gray-800 text-white hover:bg-gray-700 transition-all disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        Pay
                      </button>
                      <button
                        onClick={() => handleClear(m.MonthlyBillingId)}
                        disabled={m.Status === "Unpaid"}
                        className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-all disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                      >
                        Clear
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Billing;